

import { useClient } from 'apprise-frontend-core/client/context';
import { useEnv } from 'apprise-frontend-core/env';
import { useT } from "apprise-frontend-core/intl/language";
import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { useService } from './service';


export const sessionKey = 'apprise-session'

export const useCall = (target?: string) => {

    const client = useClient()

    const env = useEnv()
    const service = useService()

    const defaultOnError = useDefaultOnError()

    // passes request condifguration through all methods with a callback.
    const request = <D = any>(reqconfig: AxiosRequestConfig<D>) => {


        client.get().onRequest

            // purges the undefined.
            .filter(i => i)

            .forEach(interceptor => interceptor!(reqconfig))

        const currentSession = client.get().runtimeId


        if (currentSession)
            if (reqconfig.headers)
                reqconfig.headers[sessionKey] = currentSession
            else
                reqconfig.headers = { [sessionKey]: currentSession }

        return reqconfig

    }



    const error = (e: AxiosError): never => {

        // invokes all error handlers (app,modules,default), until one throws.
        [

            ...client.get().onError,

            defaultOnError

        ]

            // purges the undefined.
            .filter(i => i)

            .forEach(interceptor => interceptor(e))

        throw e;  //   throw it if no handler has yet (typechecker demands it).
    }

    // TODO: this cleanup connection error in dev mode when connecting to an http proxy via Vite.
    // for some reasons, NSCONNERROR are reported as 200 with empty responses. remove once we figure out
    // how to config the proxy to not do that. 
    const checkForProxyNoConnError = (r: AxiosResponse) => {

        if (!r.data && r.status !== 204 && env.name === 'development')
            throw new Error("no proxy connection.")

        return r
    }

    const self = {

        fq: (path: string | RegExp, targetName?: string) => {

            const target = targetName ? service.get(targetName) : service.default();
            const prefix = target.prefix;
            return `${prefix}${path}`


        }

        ,

        at: (path: string, targetName: string | undefined = target) => self.atPath(self.fq(path, targetName)),



        atPath: (path: string) => ({

            get: <T>(config: RequestConfig = {}) => client.get().impl(request({ ...config, method: 'get', url: path })).then(checkForProxyNoConnError).catch(error).then(r => config.raw ? r : r.data) as Promise<T>,
            post: <T, D = any>(data?: D, config: RequestConfig<D> = {}) => client.get().impl(request({ ...config, method: 'post', data, url: path })).catch(error).then(r => config.raw ? r : r.data) as Promise<T>,
            put: <T, D = any>(data?: D, config: RequestConfig<D> = {}) => client.get().impl(request({ ...config, method: 'put', data, url: path })).catch(error).then(r => config.raw ? r : r.data) as Promise<T>,
            delete: <D = any>(config: RequestConfig<D> = {}) => client.get().impl(request({ ...config, method: 'delete', url: path })).catch(error).then(r => config.raw ? r : r.data) as Promise<void>,

        })




    }

    return self;

}

export type RequestConfig<D = any> = AxiosRequestConfig<D> & Partial<{

    raw: boolean
}>


// internal api: default handler of edge errors and server errors with 'canonical' {message,details} payload.  
const useDefaultOnError = () => {

    const internError = useInternError()

    return (e: AxiosError<any>) => {

        throw internError(e);
    }

}


export const useInternError = () => {

    const t = useT()

    return (e: AxiosError<any>) => {

        const url = e.config?.url

        if (e.response) { //  if response is conventional, parse it. else use bare error message.

            // build a fallback message from url and available message.
            const baremsg = t("client.bare", { url, message: e.message })

            // uses the fallback unless it finds one in a `data` payload
            e.message = t(e.response.data?.message ?? baremsg)

            // force details as a custom property of axios errors
            e["details"] = e.response.data?.stacktrace ?? e.response.data ?? baremsg

        }
        else

            // builds a message in the lack of a response.
            e.message = `${e.request ? t("client.noresponse", { url }) : url ? t("client.norequest", { url }) : t("client.norequest_nourl")}: ${e.message}`

        return e
    }
}